import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { PageHeader } from '@/components/common/PageHeader'
import { KpiCard } from '@/components/common/KpiCard'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { consultorService } from '@/services/consultorService'
import {
  COR_SITUACAO_CONSULTOR,
  ConsultoresResumo,
  ConsultorResumo,
  IndicadoresConsultor,
  ROTULO_SITUACAO_CONSULTOR,
  SITUACOES_CONSULTOR,
  SituacaoConsultor,
} from '@/types/consultor'
import { ROTULO_STATUS_CANDIDATURA } from '@/types/candidatura'
import { CANAIS_COMERCIAIS, CANAL_LABEL, CanalComercial } from '@/types/statusReserva'
import { PageResponse } from '@/types/comum'
import { formatCurrency, formatDate } from '@/utils/formatters'
import { authStore } from '@/store/authStore'

export const rotuloCanal = (canal?: string | null): string =>
  canal ? (CANAL_LABEL[canal as CanalComercial] ?? canal) : '—'

/** Espera o usuário parar de digitar antes de ir ao servidor. */
const ESPERA_BUSCA_MS = 400

/**
 * Tela de Consultores (ADMIN) — `docs/PLANO_TELA_CONSULTORES.md`. Lista todos os consultores
 * (papéis CONSULTOR e CONSULTOR_ONBOARDING), com busca, situação e canal filtrados e paginados
 * no auth; os números de reservas vêm prontos do oportunidades e são juntados pelo `id`.
 * Consultor sem reserva não vem nos indicadores: a linha mostra zero.
 */
const ConsultoresListPage: FC = () => {
  const navigate = useNavigate()
  const ehAdmin = authStore((s) => !!s.usuario?.permissoes?.some((p) => p.toUpperCase() === 'ADMIN'))

  const [busca, setBusca] = useState('')
  const [buscaAplicada, setBuscaAplicada] = useState('')
  const [situacao, setSituacao] = useState<SituacaoConsultor | ''>('')
  const [canal, setCanal] = useState('')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)

  const [pagina, setPagina] = useState<PageResponse<ConsultorResumo> | null>(null)
  const [resumo, setResumo] = useState<ConsultoresResumo | null>(null)
  const [indicadores, setIndicadores] = useState<Map<string, IndicadoresConsultor> | null>(null)
  const [falhaIndicadores, setFalhaIndicadores] = useState(false)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [estadoErro, setEstadoErro] = useState<'erro' | 'semPermissao'>('erro')

  useEffect(() => {
    const t = setTimeout(() => {
      setBuscaAplicada(busca)
      setPage(0)
    }, ESPERA_BUSCA_MS)
    return () => clearTimeout(t)
  }, [busca])

  const carregarLista = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setPagina(await consultorService.listar({ busca: buscaAplicada, situacao, canal, page, size }))
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar os consultores')
      setEstadoErro((e as { status?: number })?.status === 403 ? 'semPermissao' : 'erro')
    } finally {
      setCarregando(false)
    }
  }, [buscaAplicada, situacao, canal, page, size])

  // Contagens e números de reservas não dependem do filtro: carregam uma vez (e no "Atualizar").
  const carregarApoio = useCallback(async () => {
    const [r, ind] = await Promise.allSettled([consultorService.resumo(), consultorService.indicadores()])
    setResumo(r.status === 'fulfilled' ? r.value : null)
    if (ind.status === 'fulfilled') {
      setIndicadores(new Map(ind.value.map((i) => [i.consultorId, i])))
      setFalhaIndicadores(false)
    } else {
      setIndicadores(null)
      setFalhaIndicadores(true)
    }
  }, [])

  useEffect(() => {
    if (ehAdmin) void carregarLista()
  }, [ehAdmin, carregarLista])

  useEffect(() => {
    if (ehAdmin) void carregarApoio()
  }, [ehAdmin, carregarApoio])

  const linhas = useMemo(() => pagina?.content ?? [], [pagina])
  const total = pagina?.totalElements ?? 0
  const filtrando = !!(buscaAplicada.trim() || situacao || canal)

  const numeros = (c: ConsultorResumo): IndicadoresConsultor | null | undefined =>
    indicadores ? (indicadores.get(c.id) ?? null) : undefined

  const limparFiltros = () => {
    setBusca('')
    setBuscaAplicada('')
    setSituacao('')
    setCanal('')
    setPage(0)
  }

  if (!ehAdmin) {
    return (
      <Box>
        <PageHeader title="Consultores" />
        <EstadoDeLista estado="semPermissao" detalhe="A tela de Consultores é exclusiva do administrador." />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title="Consultores"
        subtitle="Todos os consultores do portal: cadastro, situação e carteira de reservas"
        acao={
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              void carregarLista()
              void carregarApoio()
            }}
            disabled={carregando}
          >
            Atualizar
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard
            label="Consultores"
            valor={resumo ? resumo.total : '—'}
            legenda={resumo ? `${resumo.semCandidatura} criados sem candidatura` : 'Contagem indisponível'}
            variante="escura"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Ativos" valor={resumo ? resumo.ativos : '—'} legenda="Acesso liberado" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Em onboarding" valor={resumo ? resumo.emOnboarding : '—'} legenda="Na trilha do primeiro acesso" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiCard label="Inativos" valor={resumo ? resumo.inativos : '—'} legenda="Bloqueados ou inativados" />
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          label="Buscar"
          placeholder="Nome, e-mail ou CNPJ"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <TextField
          select
          size="small"
          label="Situação"
          value={situacao}
          onChange={(e) => {
            setSituacao(e.target.value as SituacaoConsultor | '')
            setPage(0)
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">Todas</MenuItem>
          {SITUACOES_CONSULTOR.map((s) => (
            <MenuItem key={s} value={s}>
              {ROTULO_SITUACAO_CONSULTOR[s]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Canal"
          value={canal}
          onChange={(e) => {
            setCanal(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {CANAIS_COMERCIAIS.map((c) => (
            <MenuItem key={c} value={c}>
              {CANAL_LABEL[c]}
            </MenuItem>
          ))}
        </TextField>
        {filtrando && (
          <Button onClick={limparFiltros} size="small">
            Limpar filtros
          </Button>
        )}
      </Stack>

      {falhaIndicadores && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Os números de reservas não carregaram agora — as colunas da carteira aparecem com “—”.
        </Alert>
      )}

      {erro ? (
        <EstadoDeLista
          estado={estadoErro}
          {...(estadoErro === 'erro' && {
            mensagem: 'Não foi possível carregar os consultores',
            detalhe: erro,
            acao: { label: 'Tentar novamente', onClick: () => void carregarLista() },
          })}
        />
      ) : carregando && !pagina ? (
        <EstadoDeLista estado="carregando" />
      ) : linhas.length === 0 ? (
        filtrando ? (
          <EstadoDeLista
            estado="vazio"
            mensagem="Nenhum consultor com esses filtros"
            acao={{ label: 'Limpar filtros', onClick: limparFiltros }}
          />
        ) : (
          <EstadoDeLista estado="vazio" mensagem="Nenhum consultor cadastrado ainda" />
        )
      ) : (
        <>
          <TabelaAcao<ConsultorResumo>
            aria-label="Consultores"
            linhas={linhas}
            chaveDaLinha={(c) => c.id}
            onAbrir={(c) => navigate(`/consultores/${c.id}`)}
            colunas={[
              {
                titulo: 'Consultor',
                render: (c) => (
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {c.nome || '(sem nome)'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {c.email ?? '—'}
                    </Typography>
                  </Box>
                ),
              },
              {
                titulo: 'Canal',
                render: (c) => (
                  <Box>
                    <Typography variant="body2">{rotuloCanal(c.tipoCanal)}</Typography>
                    {c.tenantNome && (
                      <Typography variant="caption" color="text.secondary">
                        {c.tenantNome}
                      </Typography>
                    )}
                  </Box>
                ),
              },
              {
                titulo: 'Situação',
                largura: 170,
                render: (c) => (
                  <Box>
                    <Chip
                      size="small"
                      color={COR_SITUACAO_CONSULTOR[c.situacao]}
                      label={ROTULO_SITUACAO_CONSULTOR[c.situacao] ?? c.situacao}
                    />
                    <Typography variant="caption" color="text.secondary" component="div">
                      {c.statusCadastro ? ROTULO_STATUS_CANDIDATURA[c.statusCadastro] ?? c.statusCadastro : 'Sem candidatura'}
                    </Typography>
                  </Box>
                ),
              },
              {
                titulo: 'Em andamento',
                align: 'right',
                largura: 120,
                render: (c) => {
                  const n = numeros(c)
                  return n === undefined ? '—' : (n?.emAndamento ?? 0)
                },
              },
              {
                titulo: 'Volume em aberto',
                align: 'right',
                largura: 160,
                render: (c) => {
                  const n = numeros(c)
                  return n === undefined ? '—' : formatCurrency(n?.volumeEmAberto ?? 0)
                },
              },
              {
                titulo: 'Última reserva',
                largura: 130,
                render: (c) => {
                  const n = numeros(c)
                  return n === undefined ? '—' : n?.ultimaReservaEm ? formatDate(n.ultimaReservaEm) : 'Nenhuma'
                },
              },
            ]}
          />
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => {
              setSize(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 20, 50]}
            labelRowsPerPage="Por página"
          />
        </>
      )}
    </Box>
  )
}

export default ConsultoresListPage
